"use client";

import { useContext, useState } from "react";
import { AxiosResponse } from "axios";

import { LayoutContext } from "@/contexts/LayoutContext";
import { getAuthCookie } from "@/services/CookiesService";

import { useApi } from "./useApi";

export interface UseApiWithLayoutResult<T> {
  request: (data?: any) => Promise<AxiosResponse<T>>;
  loading: boolean;
  data: AxiosResponse<T> | null;
  error: any;
}

export const useApiWithLayout = <T>(
  apiFunc: (data?: any) => Promise<AxiosResponse<T>>,
  disableLoading?: boolean
): UseApiWithLayoutResult<T> => {
  const layoutContext = useContext(LayoutContext);

  // Local state for data and error (backward compatibility)
  const [data, setData] = useState<AxiosResponse<T> | null>(null);
  const [error, setError] = useState<any>(undefined);

  const { request: baseRequest, loading } = useApi<T>(
    apiFunc,
    {
      disableLoading,
      onSuccess: (result) => {
        setData(result);
        setError(undefined);
      },
      onError: (err) => {
        setError(err.response || err);
      },
    },
    {
      loadingManager: layoutContext
        ? {
            setLoading: (loading: boolean) => layoutContext.setIsLoading(loading),
          }
        : undefined,
      authProvider: getAuthCookie,
    }
  );

  const request = async (requestData?: any): Promise<AxiosResponse<T>> => {
    return baseRequest(requestData);
  };

  return {
    request,
    loading,
    data,
    error,
  };
};
